import { FC } from 'react'
import styles from './full-news-card.module.scss'
import { INews } from '@/types/news.interface'
import SeoText from '@/components/ui/SeoText'
import SocialIcon from '@/components/icons/SocialIcon'
import removeStylesFromHtmlString from '@/utils/helpers/remove-styles.helper'
import classNames from '@/utils/helpers/class-names.helper'
import Image from 'next/image'
import Link from 'next/link'
import { useAppDispatch } from '@/redux/store'
import { setDescription, setTitle } from '@/redux/meta/slice'
import Schema from '@/components/meta/Schema'

type FullNewsCardProps = INews & {
  short?: boolean
}

const FullNewsCard: FC<FullNewsCardProps> = ({
  header,
  date,
  body,
  old_id,
  imageUrl,
  short,
  categories,
  shortBody
}) => {
  const dispatch = useAppDispatch()

  const newsDate = new Date(date)
  const newsImages = body.match(/src="([^"]+)"/g)?.map(src => src.replace('src="', '').replace('"', ''))

  const categoriesText = 
    !categories.length ? 'Новости Majordomo'
    : `${categories[0].display_name} Majordomo`

  const homeLink = `/news${categories.length ? `/${categories[0].name}` : ''}`

  const title = header.length + 20 > 60 ? header : `${header} | ${categoriesText}`
  const description = shortBody

  if (!short) {
    dispatch(setTitle(title))
    dispatch(setDescription(description))
  }

  const schema = {
    "@context": "https://schema.org",
    "@type": "NewsArticle",
    "url": `https://${process.env.NEXT_PUBLIC_DOMAIN}/news/${old_id}`,
    "publisher": {
      "@type": "Organization",
      "name": "Majordomo",
      "logo": `https://${process.env.NEXT_PUBLIC_DOMAIN}/img/short-logo.svg`
    },
    "headline": header,
    "datePublished": newsDate.toISOString(),
    "articleBody": removeStylesFromHtmlString(body),
    "image": [
      imageUrl,
      ...(newsImages || []),
    ],
  }

  return (
    <div>
      <Schema schema={schema} />
      <div className={styles['snippet']}>
        <Link href={homeLink} className={styles['snippet-author']}>
          <div className={styles['snippet-author-logo']}>
            <Image src={'/img/short-logo.svg'} alt="Majordomo - Хостинг-провайдер" title="Majordomo - Хостинг-провайдер" width={32} height={32} loading="lazy" />
          </div>
          <div className={styles['snippet-author-name']}>
            {categoriesText}
          </div>
        </Link>
        <time className={styles['snippet-date']} dateTime={newsDate.toISOString()}>
          {newsDate.toLocaleDateString('ru')}
        </time>
      </div>
      <div className={styles['heading-wrapper']}>
        <h1 className={classNames(styles.heading, 'heading', '_main')}>{header}</h1>
        {!short && <Image 
          src={imageUrl || '/img/news_default_picture.jpg'} 
          alt={header}
          title={header}
          className={styles.image} 
          width={1000} 
          height={1000} 
          loading="lazy"
          placeholder='blur'
          blurDataURL="data:image/jpeg;base64,/9j/4AAQSkZJRgABAQAAAQABAAD/2wBDAAYEBQYFBAYGBQYHBwYIChAKCgkJChQODwwQFxQYGBcUFhYaHSUfGhsjHBYWICwgIyYnKSopGR8tMC0oMCUoKSj/2wBDAQcHBwoIChMKChMoGhYaKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCj/wAARCAABAAEDASIAAhEBAxEB/8QAFQABAQAAAAAAAAAAAAAAAAAAAAv/xAAUEAEAAAAAAAAAAAAAAAAAAAAA/8QAFQEBAQAAAAAAAAAAAAAAAAAAAAX/xAAUEQEAAAAAAAAAAAAAAAAAAAAA/9oADAMBAAIRAxEAPwCdABmX/9k="
          sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
        />}
      </div>
      <SeoText className={styles.content}>
        <article>
          <SeoText dangerouslySetInnerHTML={{ __html: removeStylesFromHtmlString(body) }} />
        </article>
      </SeoText>
      <div className={classNames(styles.socials, 'nsc')}>
        <p>Поделиться:</p>
        <div className={styles['socials-links']}>
          <a
            href={`https://t.me/share?url=https://www.majordomo.ru/news/${old_id}&text=${header}&utm_source=share2`}
            rel="nofollow noopener"
            target="_blank"
          >
            <SocialIcon variant="telegram" width={18} height={18} />
          </a>
          <a
            href={`https://connect.ok.ru/offer?url=https://www.majordomo.ru/news/${old_id}&title=${header}&utm_source=share2`}
            rel="nofollow noopener"
            target="_blank"
          >
            <SocialIcon variant="ok" width={24} height={24} />
          </a>
          <a
            href={`https://vk.com/share.php?url=https://www.majordomo.ru/news/${old_id}&title=${header}&utm_source=share2`}
            rel="nofollow noopener"
            target="_blank"
          >
            <SocialIcon variant="vk" width={24} height={24} />
          </a>
          <a
            href={`https://twitter.com/intent/tweet?url=https://www.majordomo.ru/news/${old_id}&text=${header}&utm_source=share2`}
            rel="nofollow noopener"
            target="_blank"
          >
            <SocialIcon variant="twitter" fillCurrent="fill-accent" width={24} height={24} />
          </a>
        </div>
      </div>
    </div>
  )
}

export default FullNewsCard
